/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83): the buttons on the right of the call header —
 * participants in a group call (08·06 → 08·07), "Chat" in a video or group
 * call when ION_CHAT_IN_HEADER is on and the app handles it, and the gear
 * for the call settings.
 */

import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from "@vector-im/compound-web";
import {
  ChatIcon,
  GroupIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import { SettingsIconButton } from "../button";
import { pick } from "./callErrorTexts";
import { CALL_SCREEN_TEXTS as T, ION_CHAT_IN_HEADER } from "./callScreen";
import { callMode } from "./IonCallControls";
import { useIonHostActions } from "./hostActions";
import styles from "./IonHeaderButtons.module.css";

interface Props {
  roomId: string;
  participants: number;
  videoEnabled: boolean;
  onParticipants: () => void;
  onSettings: () => void;
}

export const IonHeaderButtons: FC<Props> = ({
  roomId,
  participants,
  videoEnabled,
  onParticipants,
  onSettings,
}) => {
  const { i18n } = useTranslation();
  const l = i18n.language;
  const host = useIonHostActions();
  const mode = callMode(participants, videoEnabled);
  return (
    <div className={styles.buttons}>
      {mode === "group" && (
        <Tooltip label={pick(T.participants, l)}>
          <IconButton
            aria-label={pick(T.participants, l)}
            onClick={onParticipants}
          >
            <GroupIcon />
          </IconButton>
        </Tooltip>
      )}
      {ION_CHAT_IN_HEADER && mode !== "audio" && host.can("open_chat") && (
        <Tooltip label={pick(T.chat, l)}>
          <IconButton
            aria-label={pick(T.chat, l)}
            onClick={() => host.send("open_chat", { room_id: roomId })}
          >
            <ChatIcon />
          </IconButton>
        </Tooltip>
      )}
      <SettingsIconButton onClick={onSettings} data-testid="settings-app-bar" />
    </div>
  );
};
